import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import type { FormEvent, ReactNode } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export type AuthMode = "password" | "reset" | "sms";
export type SmsStep = "phone" | "code";
export type ResetStep = "request" | "code";

export interface LoginFormsProps {
  authMode: AuthMode;
  formError: string | null;
  isFormDisabled: boolean;
  isSubmitting: boolean;
  onAuthModeChange: (mode: AuthMode) => void;
  onPasswordSubmit: (event: FormEvent<HTMLFormElement>) => void;
  onResetReset: () => void;
  onResetSubmit: (event: FormEvent<HTMLFormElement>) => void;
  onSmsPhoneChange: (phone: string) => void;
  onSmsSubmit: (event: FormEvent<HTMLFormElement>) => void;
  onUseAnotherNumber: () => void;
  resetCompleteEmail: string | null;
  resetEmail: string;
  resetStep: ResetStep;
  smsPhone: string;
  smsStep: SmsStep;
}

function LoginFormError({ formError }: { formError: string | null }) {
  if (!formError) {
    return null;
  }

  return (
    <Alert
      className="border-destructive/40 bg-destructive/10 text-foreground"
      id="login-form-error"
      role="alert"
    >
      <AlertDescription>{formError}</AlertDescription>
    </Alert>
  );
}

function AuthModeTabs({
  authMode,
  isFormDisabled,
  onAuthModeChange,
}: Pick<LoginFormsProps, "authMode" | "isFormDisabled" | "onAuthModeChange">) {
  return (
    <div className="flex rounded-2xl border border-border bg-muted p-1">
      <Button
        aria-pressed={authMode === "password"}
        className="h-[44px] flex-1 rounded-xl sm:h-10"
        disabled={isFormDisabled}
        onClick={() => onAuthModeChange("password")}
        type="button"
        variant={authMode === "password" ? "default" : "ghost"}
      >
        Email
      </Button>
      <Button
        aria-pressed={authMode === "sms"}
        className="h-[44px] flex-1 rounded-xl sm:h-10"
        disabled={isFormDisabled}
        onClick={() => onAuthModeChange("sms")}
        type="button"
        variant={authMode === "sms" ? "default" : "ghost"}
      >
        SMS
      </Button>
    </div>
  );
}

function PasswordLoginForm({
  formError,
  isFormDisabled,
  isSubmitting,
  onAuthModeChange,
  onPasswordSubmit,
}: Pick<
  LoginFormsProps,
  "formError" | "isFormDisabled" | "isSubmitting" | "onAuthModeChange" | "onPasswordSubmit"
>) {
  return (
    <form
      aria-describedby={formError ? "login-form-error" : undefined}
      aria-label="Sign in"
      className="flex flex-col gap-3 text-left"
      onSubmit={onPasswordSubmit}
    >
      <div className="space-y-2">
        <label
          className="font-medium text-[0.6875rem] text-muted-foreground uppercase tracking-[0.08em]"
          htmlFor="login-email"
        >
          Email
        </label>
        <Input
          autoComplete="email"
          className="h-12"
          disabled={isFormDisabled}
          id="login-email"
          name="email"
          placeholder="operator@xan.bz"
          required
          type="email"
        />
      </div>

      <div className="space-y-2">
        <label
          className="font-medium text-[0.6875rem] text-muted-foreground uppercase tracking-[0.08em]"
          htmlFor="login-password"
        >
          Password
        </label>
        <Input
          autoComplete="current-password"
          className="h-12"
          disabled={isFormDisabled}
          id="login-password"
          name="password"
          required
          type="password"
        />
      </div>

      <LoginFormError formError={formError} />

      <Button
        className="group mt-2 h-14 w-full rounded-2xl px-8 text-base"
        disabled={isFormDisabled}
        size="lg"
        type="submit"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="mr-2 size-5 animate-spin motion-reduce:animate-none" />
            Signing in…
          </>
        ) : (
          <>
            Enter xan
            <ArrowRight className="ml-2 size-5 transition-transform group-hover:translate-x-0.5 motion-reduce:transition-none" />
          </>
        )}
      </Button>

      <Button
        className="h-[44px] w-full text-muted-foreground hover:text-foreground sm:h-9"
        disabled={isFormDisabled}
        onClick={() => onAuthModeChange("reset")}
        type="button"
        variant="link"
      >
        Forgot password?
      </Button>
    </form>
  );
}

/** Back-button + titled frame for the reset sub-flow. */
function AuthBackFrame({
  children,
  isFormDisabled,
  onBack,
  subtitle,
  title,
}: {
  readonly children: ReactNode;
  readonly isFormDisabled: boolean;
  readonly onBack: () => void;
  readonly subtitle: string;
  readonly title: string;
}) {
  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center justify-between">
        <Button
          className="h-[44px] rounded-xl px-3 text-sm sm:h-10"
          disabled={isFormDisabled}
          onClick={onBack}
          type="button"
          variant="ghost"
        >
          <ArrowLeft className="size-4" />
          Back
        </Button>
        <div className="text-right">
          <p className="font-medium text-foreground text-sm">{title}</p>
          <p className="text-muted-foreground text-xs">{subtitle}</p>
        </div>
      </div>
      {children}
    </div>
  );
}

/** Terminal "done, go sign in" notice for the reset sub-flow. */
function AuthCompletionNotice({
  children,
  onBack,
}: {
  readonly children: ReactNode;
  readonly onBack: () => void;
}) {
  return (
    <div className="flex flex-col gap-4 text-left">
      <Alert className="border-primary/40 bg-primary/10" role="status">
        <AlertDescription>{children}</AlertDescription>
      </Alert>
      <Button className="rounded-xl" onClick={onBack} size="touch" type="button" variant="outline">
        Back to sign in
      </Button>
    </div>
  );
}

/**
 * Submit-button label for the two-step SMS and reset flows: a spinner + busy
 * text while submitting, otherwise the step's call-to-action + arrow.
 */
function StepSubmitLabel({
  atFirstStep,
  busyFirst,
  busySecond,
  idleFirst,
  idleSecond,
  isSubmitting,
}: {
  readonly atFirstStep: boolean;
  readonly busyFirst: string;
  readonly busySecond: string;
  readonly idleFirst: string;
  readonly idleSecond: string;
  readonly isSubmitting: boolean;
}) {
  if (isSubmitting) {
    return (
      <>
        <Loader2 className="mr-2 size-5 animate-spin motion-reduce:animate-none" />
        {atFirstStep ? busyFirst : busySecond}
      </>
    );
  }

  return (
    <>
      {atFirstStep ? idleFirst : idleSecond}
      <ArrowRight className="ml-2 size-5 transition-transform group-hover:translate-x-0.5 motion-reduce:transition-none" />
    </>
  );
}

function SmsLoginForm({
  formError,
  isFormDisabled,
  isSubmitting,
  onSmsPhoneChange,
  onSmsSubmit,
  onUseAnotherNumber,
  smsPhone,
  smsStep,
}: Pick<
  LoginFormsProps,
  | "formError"
  | "isFormDisabled"
  | "isSubmitting"
  | "onSmsPhoneChange"
  | "onSmsSubmit"
  | "onUseAnotherNumber"
  | "smsPhone"
  | "smsStep"
>) {
  return (
    <form
      aria-describedby={formError ? "login-form-error" : undefined}
      aria-label="Sign in with SMS"
      className="flex flex-col gap-3 text-left"
      onSubmit={onSmsSubmit}
    >
      <div className="space-y-2">
        <label
          className="font-medium text-[0.6875rem] text-muted-foreground uppercase tracking-[0.08em]"
          htmlFor="login-phone"
        >
          Phone
        </label>
        <Input
          autoComplete="tel"
          className="h-12"
          defaultValue={smsPhone}
          disabled={isFormDisabled || smsStep === "code"}
          id="login-phone"
          name="phone"
          onChange={(event) => onSmsPhoneChange(event.target.value)}
          placeholder="(555) 123-4567"
          required
          type="tel"
        />
      </div>

      {smsStep === "code" ? (
        <div className="space-y-2">
          <label
            className="font-medium text-[0.6875rem] text-muted-foreground uppercase tracking-[0.08em]"
            htmlFor="login-code"
          >
            Code
          </label>
          <Input
            autoComplete="one-time-code"
            className="h-12"
            disabled={isFormDisabled}
            id="login-code"
            inputMode="numeric"
            maxLength={6}
            name="code"
            pattern="[0-9]*"
            required
          />
        </div>
      ) : null}

      <LoginFormError formError={formError} />

      <Button
        className="group mt-2 h-14 w-full rounded-2xl px-8 text-base"
        disabled={isFormDisabled}
        size="lg"
        type="submit"
      >
        <StepSubmitLabel
          atFirstStep={smsStep === "phone"}
          busyFirst="Sending…"
          busySecond="Verifying…"
          idleFirst="Send code"
          idleSecond="Enter xan"
          isSubmitting={isSubmitting}
        />
      </Button>
      {smsStep === "code" ? (
        <Button
          className="h-[44px] text-muted-foreground sm:h-9"
          disabled={isFormDisabled}
          onClick={onUseAnotherNumber}
          type="button"
          variant="ghost"
        >
          Use another number
        </Button>
      ) : null}
    </form>
  );
}

function ResetPasswordForm({
  formError,
  isFormDisabled,
  isSubmitting,
  onAuthModeChange,
  onResetSubmit,
  resetEmail,
  resetStep,
}: Pick<
  LoginFormsProps,
  | "formError"
  | "isFormDisabled"
  | "isSubmitting"
  | "onAuthModeChange"
  | "onResetSubmit"
  | "resetEmail"
  | "resetStep"
>) {
  return (
    <AuthBackFrame
      isFormDisabled={isFormDisabled}
      onBack={() => onAuthModeChange("password")}
      subtitle="Set a new password with the emailed code"
      title="Reset password"
    >
      <form
        aria-describedby={formError ? "login-form-error" : undefined}
        aria-label="Reset password"
        className="flex flex-col gap-3 text-left"
        onSubmit={onResetSubmit}
      >
        <div className="space-y-2">
          <label
            className="font-medium text-[0.6875rem] text-muted-foreground uppercase tracking-[0.08em]"
            htmlFor="reset-email"
          >
            Email
          </label>
          <Input
            autoComplete="email"
            className="h-12"
            defaultValue={resetEmail}
            disabled={isFormDisabled || resetStep === "code"}
            id="reset-email"
            name="email"
            placeholder="operator@xan.bz"
            required
            type="email"
          />
        </div>

        {resetStep === "code" ? (
          <>
            <div className="space-y-2">
              <label
                className="font-medium text-[0.6875rem] text-muted-foreground uppercase tracking-[0.08em]"
                htmlFor="reset-code"
              >
                Reset code
              </label>
              <Input
                autoComplete="one-time-code"
                className="h-12"
                disabled={isFormDisabled}
                id="reset-code"
                inputMode="numeric"
                maxLength={6}
                name="code"
                pattern="[0-9]*"
                required
              />
            </div>

            <div className="space-y-2">
              <label
                className="font-medium text-[0.6875rem] text-muted-foreground uppercase tracking-[0.08em]"
                htmlFor="reset-password"
              >
                New password
              </label>
              <Input
                autoComplete="new-password"
                className="h-12"
                disabled={isFormDisabled}
                id="reset-password"
                minLength={6}
                name="password"
                required
                type="password"
              />
            </div>
          </>
        ) : null}

        <LoginFormError formError={formError} />

        <Button
          className="group mt-2 h-14 w-full rounded-2xl px-8 text-base"
          disabled={isFormDisabled}
          size="lg"
          type="submit"
        >
          <StepSubmitLabel
            atFirstStep={resetStep === "request"}
            busyFirst="Sending…"
            busySecond="Saving…"
            idleFirst="Send reset code"
            idleSecond="Set password"
            isSubmitting={isSubmitting}
          />
        </Button>
        {resetStep === "request" ? (
          <Button disabled={isFormDisabled} type="submit" value="existing-code" variant="ghost">
            I already have a code
          </Button>
        ) : null}
      </form>
    </AuthBackFrame>
  );
}

/** Renders the active auth form for the login controller. */
function ActiveAuthForm(props: LoginFormsProps) {
  if (props.authMode === "password") {
    return (
      <PasswordLoginForm
        formError={props.formError}
        isFormDisabled={props.isFormDisabled}
        isSubmitting={props.isSubmitting}
        onAuthModeChange={props.onAuthModeChange}
        onPasswordSubmit={props.onPasswordSubmit}
      />
    );
  }
  if (props.authMode === "reset") {
    return (
      <ResetPasswordForm
        formError={props.formError}
        isFormDisabled={props.isFormDisabled}
        isSubmitting={props.isSubmitting}
        onAuthModeChange={props.onAuthModeChange}
        onResetSubmit={props.onResetSubmit}
        resetEmail={props.resetEmail}
        resetStep={props.resetStep}
      />
    );
  }
  return (
    <SmsLoginForm
      formError={props.formError}
      isFormDisabled={props.isFormDisabled}
      isSubmitting={props.isSubmitting}
      onSmsPhoneChange={props.onSmsPhoneChange}
      onSmsSubmit={props.onSmsSubmit}
      onUseAnotherNumber={props.onUseAnotherNumber}
      smsPhone={props.smsPhone}
      smsStep={props.smsStep}
    />
  );
}

/** Renders the active auth form or a completion state for the login controller. */
export function LoginForms(props: LoginFormsProps) {
  if (props.resetCompleteEmail) {
    return (
      <AuthCompletionNotice onBack={props.onResetReset}>
        Your password is set for {props.resetCompleteEmail}. Sign in with your email and new
        password.
      </AuthCompletionNotice>
    );
  }

  const hideTabs = props.authMode === "reset";

  return (
    <div className="flex flex-col gap-5">
      {hideTabs ? null : (
        <AuthModeTabs
          authMode={props.authMode}
          isFormDisabled={props.isFormDisabled}
          onAuthModeChange={props.onAuthModeChange}
        />
      )}

      <ActiveAuthForm {...props} />

      <p className="text-center text-muted-foreground text-xs">
        Access requires an approved operator account.
      </p>
    </div>
  );
}
